import {
  QuerySortDirection,
  type QueryFilter,
  type QueryResult,
  QuerySortField,
  type QueryQuery,
} from "@/API";
import type { State } from "@/types/common";
import { create } from "zustand";
import { generateClient, type GraphQLResult } from "aws-amplify/api";
import { query } from "@/graphql/queries";
import { GraphQLError } from "graphql";
import { triggerEvent } from "@/lib/utils";
import { setActionTriggerNeedCore } from "./restrictedFeatureState";

const client = generateClient();

export const restaurantsExplorerState = create<State<QueryResult>>(() => ({
  data: null,
  isLoading: false,
  error: null,
  isFetching: false,
}));

export const getRestaurantsExplorerIsloading = () =>
  restaurantsExplorerState.setState(() => ({
    isLoading: true,
    isFetching: false,
    error: null,
  }));
export const getRestaurantsExplorerIsFetching = () =>
  restaurantsExplorerState.setState(() => ({
    isLoading: false,
    isFetching: true,
    error: null,
  }));
export const getRestaurantsExplorerIsError = (error: GraphQLError[]) =>
  restaurantsExplorerState.setState(() => ({
    error: error,
    isLoading: false,
    isFetching: false,
  }));
export const getRestaurantsExplorerIsSuccess = (queryResult: QueryResult) =>
  restaurantsExplorerState.setState(() => ({
    data: queryResult,
    isLoading: false,
    error: null,
    isFetching: false,
  }));

export const getRestaurantsExplorer = async (
  queryFilters: QueryFilter[],
  querySortField: QuerySortField | undefined,
  append = false,
) => {
  if (append) {
    getRestaurantsExplorerIsFetching();
  }

  if (!append) {
    getRestaurantsExplorerIsloading();
  }

  try {
    const { data } = await client.graphql({
      query: query,
      variables: {
        filters: queryFilters,
        sort: querySortField
          ? {
              field: querySortField,
              direction:
                querySortField === QuerySortField.DELIVERY_TIME
                  ? QuerySortDirection.ASC
                  : QuerySortDirection.DESC,
            }
          : undefined,
        offset: append
          ? (restaurantsExplorerState.getState().data?.data.length ?? 0)
          : 0,
      },
    });

    if (!append) {
      getRestaurantsExplorerIsSuccess(data.query as QueryResult);
    }

    if (append) {
      getRestaurantsExplorerIsSuccess({
        data: [
          ...(restaurantsExplorerState.getState().data?.data ?? []),
          ...data.query.data,
        ],
        total: data.query.total,
      } as QueryResult);
    }

    triggerEvent("search_performed", {
      results_count: data.query.total,
      search_type: queryFilters,
    });
  } catch (e: unknown) {
    const graphQl = e as GraphQLResult<QueryQuery>;
    getRestaurantsExplorerIsError(graphQl.errors as GraphQLError[]);
    const restaurantsError = graphQl.errors;
    if (
      restaurantsError &&
      !(restaurantsError instanceof Error) &&
      restaurantsError[0] &&
      restaurantsError[0].message === "Free search quota exceeded"
    ) {
      setActionTriggerNeedCore(true);
    }
  }
};
